"use strict";

import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Grid, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SimpleVideoPlayer, {
    SimpleVideoPlayerClockGranularity,
    SimpleVideoPlayerPlaybackEventType,
} from "../../Components/Players/SimpleVideoPlayer";

const SAMPLE_VIDEO_URL = "https://archive.org/download/sprite-fright-2021/Sprite%20Fright%20%282021%29.mp4";
const SAMPLE_AUDIO_SOURCE_ONE_URL =
    "https://ia802309.us.archive.org/34/items/marty_robbins-gunfighter_ballads-vinyl-1959/A1%20-%20Big%20Iron.mp3?cnt=0";

const CLOCK_GRANULARITIES = SimpleVideoPlayerClockGranularity;
const PLAYBACK_EVENT_TYPES = SimpleVideoPlayerPlaybackEventType;

const CLOCK_LABELS = {
    [CLOCK_GRANULARITIES.SECOND]: "Every 1 second",
    [CLOCK_GRANULARITIES.HUNDRED_MS]: "Every 100 ms",
    [CLOCK_GRANULARITIES.TEN_MS]: "Every 10 ms",
    [CLOCK_GRANULARITIES.FRAME]: "Each video frame",
};

const PLAYBACK_EVENT_LABELS = {
    [PLAYBACK_EVENT_TYPES.STARTED]: "Playback started",
    [PLAYBACK_EVENT_TYPES.PAUSED]: "Playback paused",
    [PLAYBACK_EVENT_TYPES.ENDED]: "Playback ended",
    [PLAYBACK_EVENT_TYPES.SEEKED]: "Playback seeked",
    [PLAYBACK_EVENT_TYPES.INTERRUPTED_BUFFERING]: "Buffering interruption",
    [PLAYBACK_EVENT_TYPES.INTERRUPTED_CONNECTION]: "Connection interruption",
    [PLAYBACK_EVENT_TYPES.RESUMED]: "Playback resumed",
    [PLAYBACK_EVENT_TYPES.BUFFER_SIZE_INCREASED]: "Buffer size increased",
};

const MAX_PLAYBACK_LOG_LENGTH = 20;

const createInitialSnapshotState = () => ({
    [CLOCK_GRANULARITIES.SECOND]: null,
    [CLOCK_GRANULARITIES.HUNDRED_MS]: null,
    [CLOCK_GRANULARITIES.TEN_MS]: null,
    [CLOCK_GRANULARITIES.FRAME]: null,
});

const formatSecondsValue = (value) => {
    if (!Number.isFinite(value)) {
        return "--";
    }

    return value.toFixed(3);
};

const formatMillisecondsValue = (value) => {
    if (!Number.isFinite(value)) {
        return "--";
    }

    return (value * 1000).toFixed(1);
};

const createAudioTracksFromInputs = (primarySource, secondarySource) => {
    const normalizedPrimary = (primarySource || "").trim();
    const normalizedSecondary = (secondarySource || "").trim();
    const entries = [];

    if (normalizedPrimary) {
        entries.push({
            id: "alt-audio-1",
            label: "Alternate audio 1",
            src: normalizedPrimary,
        });
    }

    if (normalizedSecondary) {
        entries.push({
            id: "alt-audio-2",
            label: "Alternate audio 2",
            src: normalizedSecondary,
        });
    }

    return entries;
};

const describePlaybackEventSource = (event) => {
    if (!event) {
        return "Unknown source";
    }

    const label = event.mediaSourceLabel || "Unknown source";
    if (event.mediaElement) {
        return `${label} (${event.mediaElement})`;
    }

    return label;
};

const formatPlaybackEventHeadline = (event) => {
    if (!event) {
        return "Waiting for playback events...";
    }

    const typeLabel = PLAYBACK_EVENT_LABELS[event.type] || event.type;
    const originLabel = event.origin === "user" ? "User initiated" : "System triggered";
    const sourceLabel = describePlaybackEventSource(event);

    if (event.type === PLAYBACK_EVENT_TYPES.SEEKED) {
        const target = Number.isFinite(event.targetTime)
            ? `${formatSecondsValue(event.targetTime)} s`
            : "unknown";
        return `${typeLabel} to ${target} • ${sourceLabel} • ${originLabel}`;
    }

    if (event.type === PLAYBACK_EVENT_TYPES.RESUMED) {
        const resumedFrom = event.resumedFrom
            ? (PLAYBACK_EVENT_LABELS[event.resumedFrom] || event.resumedFrom)
            : "interruption";
        return `${typeLabel} after ${resumedFrom.toLowerCase()} • ${sourceLabel} • ${originLabel}`;
    }

    if (event.type === PLAYBACK_EVENT_TYPES.BUFFER_SIZE_INCREASED) {
        const bufferSize = Number.isFinite(event.bufferSizeSeconds)
            ? `${formatSecondsValue(event.bufferSizeSeconds)} s`
            : "updated";
        return `${typeLabel} to ${bufferSize} • ${sourceLabel} • ${originLabel}`;
    }

    return `${typeLabel} • ${sourceLabel} • ${originLabel}`;
};

const buildPlaybackEventDetailLines = (event) => {
    if (!event) {
        return [];
    }

    const lines = [];
    const playbackTime = Number.isFinite(event.currentTime)
        ? `${formatSecondsValue(event.currentTime)} s`
        : "--";
    lines.push(`Playback position: ${playbackTime}`);

    if (event.type === PLAYBACK_EVENT_TYPES.SEEKED) {
        if (Number.isFinite(event.targetTime)) {
            lines.push(`Seek target: ${formatSecondsValue(event.targetTime)} s`);
        }
        if (Number.isFinite(event.requestedTime) && event.requestedTime !== event.targetTime) {
            lines.push(`Requested time: ${formatSecondsValue(event.requestedTime)} s`);
        }
    }

    if (event.type === PLAYBACK_EVENT_TYPES.INTERRUPTED_BUFFERING) {
        if (Number.isFinite(event.readyState)) {
            lines.push(`Ready state: ${event.readyState}`);
        }
        lines.push("Reason: Buffering");
    }

    if (event.type === PLAYBACK_EVENT_TYPES.INTERRUPTED_CONNECTION) {
        if (event.errorCode !== undefined) {
            lines.push(`Error code: ${event.errorCode}`);
        }
        if (event.errorMessage) {
            lines.push(`Error message: ${event.errorMessage}`);
        }
        lines.push("Reason: Connection failure");
    }

    if (event.type === PLAYBACK_EVENT_TYPES.RESUMED && event.resumedFrom) {
        const resumedLabel = PLAYBACK_EVENT_LABELS[event.resumedFrom] || event.resumedFrom;
        lines.push(`Resumed after: ${resumedLabel.toLowerCase()}`);
    }

    if (event.type === PLAYBACK_EVENT_TYPES.BUFFER_SIZE_INCREASED) {
        if (Number.isFinite(event.bufferSizeSeconds)) {
            lines.push(`Buffer size: ${formatSecondsValue(event.bufferSizeSeconds)} s`);
        }
    }

    return lines;
};

const formatPlaybackEventLogEntry = (event) => {
    const receivedAt = new Date(event.timestamp).toLocaleTimeString();
    const originLabel = event.origin === "user" ? "User" : "System";
    const sourceLabel = describePlaybackEventSource(event);
    const detailLines = buildPlaybackEventDetailLines(event);
    const detailsText = detailLines.length > 0 ? ` • ${detailLines.join(" • ")}` : "";

    return `${formatPlaybackEventHeadline(event)} • Received ${receivedAt} • Origin: ${originLabel} • Source: ${sourceLabel}${detailsText}`;
};

const SimpleVideoPlayerDemo = () => {
    const [videoInputValue, setVideoInputValue] = useState(SAMPLE_VIDEO_URL);
    const [audioInputOne, setAudioInputOne] = useState(SAMPLE_AUDIO_SOURCE_ONE_URL);
    const [audioInputTwo, setAudioInputTwo] = useState("");
    const [activeSource, setActiveSource] = useState(SAMPLE_VIDEO_URL);
    const [activeAudioTracks, setActiveAudioTracks] = useState(() =>
        createAudioTracksFromInputs(SAMPLE_AUDIO_SOURCE_ONE_URL, ""),
    );
    const [clockSnapshots, setClockSnapshots] = useState(() => createInitialSnapshotState());
    const [clockMeta, setClockMeta] = useState({ duration: 0, isPlaying: false });
    const [isClockReady, setIsClockReady] = useState(false);
    const subscriptionsRef = useRef([]);
    const [recentPlaybackEvent, setRecentPlaybackEvent] = useState(null);
    const [playbackEventsLog, setPlaybackEventsLog] = useState([]);
    const [isClockPanelCollapsed, setIsClockPanelCollapsed] = useState(false);
    const [isPlaybackPanelCollapsed, setIsPlaybackPanelCollapsed] = useState(false);
    const playbackSubscriptionRef = useRef(null);

    // Normalize the submitted URL and update the active video source.
    const handleSubmit = (event) => {
        event.preventDefault();
        const trimmedVideoSource = videoInputValue.trim();
        const trimmedAudioOne = audioInputOne.trim();
        const trimmedAudioTwo = audioInputTwo.trim();

        if (trimmedVideoSource) {
            setActiveSource(trimmedVideoSource);
            setActiveAudioTracks(
                createAudioTracksFromInputs(trimmedAudioOne, trimmedAudioTwo),
            );
        }
    };

    // Restore the demo to the built-in sample clip.
    const handleReset = () => {
        setVideoInputValue(SAMPLE_VIDEO_URL);
        setAudioInputOne(SAMPLE_AUDIO_SOURCE_ONE_URL);
        setAudioInputTwo("");
        setActiveSource(SAMPLE_VIDEO_URL);
        setActiveAudioTracks(createAudioTracksFromInputs(SAMPLE_AUDIO_SOURCE_ONE_URL, ""));
    };

    const cleanupClockSubscriptions = useCallback(() => {
        subscriptionsRef.current.forEach((unsubscribe) => unsubscribe());
        subscriptionsRef.current = [];
    }, []);

    const cleanupPlaybackSubscription = useCallback(() => {
        if (playbackSubscriptionRef.current) {
            playbackSubscriptionRef.current();
            playbackSubscriptionRef.current = null;
        }
    }, []);

    // React to the master clock lifecycle and wire sample subscriptions for the demo UI.
    const handleClockReady = useCallback((clockApi) => {
        // Tear down previous subscriptions before establishing new ones.
        cleanupClockSubscriptions();

        if (!clockApi) {
            setIsClockReady(false);
            setClockSnapshots(createInitialSnapshotState());
            setClockMeta({ duration: 0, isPlaying: false });
            return;
        }

        setIsClockReady(true);
        setClockMeta({
            duration: clockApi.getDuration(),
            isPlaying: clockApi.isPlaying(),
        });

        // Convenience helper for binding and tracking each requested granularity.
        const subscribeToGranularity = (granularity) => {
            const unsubscribe = clockApi.subscribe(
                granularity,
                (payload) => {
                    setClockSnapshots((previous) => {
                        const priorSnapshot = previous[granularity];
                        const nextCount = priorSnapshot ? priorSnapshot.count + 1 : 1;

                        return {
                            ...previous,
                            [granularity]: {
                                currentTime: payload.currentTime,
                                delta: payload.delta ?? 0,
                                frame: payload.frame ?? null,
                                updatedAt: Date.now(),
                                count: nextCount,
                            },
                        };
                    });

                    if (granularity === CLOCK_GRANULARITIES.SECOND) {
                        // Poll the API for status metadata at a relaxed cadence.
                        setClockMeta({
                            duration: clockApi.getDuration(),
                            isPlaying: clockApi.isPlaying(),
                        });
                    }
                },
                {
                    // Defer the initial emit for coarse intervals so the demo reflects
                    // the real cadence instead of an immediate zero-delta event.
                    fireImmediately: ![CLOCK_GRANULARITIES.SECOND, CLOCK_GRANULARITIES.HUNDRED_MS].includes(
                        granularity,
                    ),
                },
            );

            subscriptionsRef.current.push(unsubscribe);
        };

        // Register subscribers for every demo granularity option.
        subscribeToGranularity(CLOCK_GRANULARITIES.SECOND);
        subscribeToGranularity(CLOCK_GRANULARITIES.HUNDRED_MS);
        subscribeToGranularity(CLOCK_GRANULARITIES.TEN_MS);
        subscribeToGranularity(CLOCK_GRANULARITIES.FRAME);
    }, [cleanupClockSubscriptions]);

    const handlePlaybackEventsReady = useCallback((playbackApi) => {
        cleanupPlaybackSubscription();
        setRecentPlaybackEvent(null);
        setPlaybackEventsLog([]);

        if (!playbackApi) {
            return;
        }

        const unsubscribe = playbackApi.subscribe((event) => {
            setRecentPlaybackEvent(event);
            setPlaybackEventsLog((previous) => {
                const next = [event, ...previous];
                return next.slice(0, MAX_PLAYBACK_LOG_LENGTH);
            });
        });

        playbackSubscriptionRef.current = unsubscribe;
    }, [cleanupPlaybackSubscription]);

    useEffect(() => () => cleanupClockSubscriptions(), [cleanupClockSubscriptions]);
    useEffect(() => () => cleanupPlaybackSubscription(), [cleanupPlaybackSubscription]);

    // Render the input form alongside the video player preview.
    return (
        <Box sx={{ padding: { xs: 2, md: 4 }, maxWidth: "1200px", margin: "0 auto" }}>
            <Stack spacing={4}>
                <Paper sx={{ padding: { xs: 2, md: 3 } }} elevation={2}>
                    <form onSubmit={handleSubmit}>
                        <Stack spacing={2}>
                            <Typography variant="h5" component="h1">
                                Simple Video Player Demo
                            </Typography>
                            <Typography variant="body1">
                                Paste the URL to any browser-playable video source and load it below to preview the
                                new layout-compatible player in isolation.
                            </Typography>
                            <TextField
                                label="Video URL"
                                type="url"
                                value={videoInputValue}
                                onChange={(event) => setVideoInputValue(event.target.value)}
                                placeholder="https://example.com/video.mp4"
                                fullWidth
                                required
                            />
                            <TextField
                                label="Additional audio source 1"
                                type="url"
                                value={audioInputOne}
                                onChange={(event) => setAudioInputOne(event.target.value)}
                                placeholder="https://example.com/audio-track.mp3"
                                fullWidth
                            />
                            <TextField
                                label="Additional audio source 2"
                                type="url"
                                value={audioInputTwo}
                                onChange={(event) => setAudioInputTwo(event.target.value)}
                                placeholder="https://example.com/second-audio.mp3"
                                fullWidth
                            />
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                alignItems={{ xs: "stretch", sm: "center" }}
                            >
                                <Button type="submit" variant="contained" disabled={!videoInputValue.trim()}>
                                    Load Media
                                </Button>
                                <Button type="button" variant="outlined" onClick={handleReset}>
                                    Reset to sample clip
                                </Button>
                            </Stack>
                        </Stack>
                    </form>
                </Paper>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                backgroundColor: "common.white",
                                borderRadius: "var(--radius-md)",
                                padding: { xs: 2, md: 3 },
                                boxShadow: 1,
                            }}
                        >
                            <SimpleVideoPlayer
                                source={activeSource}
                                layout="horizontal"
                                enableScroll
                                audioTracks={activeAudioTracks}
                                onClockReady={handleClockReady}
                                onPlaybackEventsReady={handlePlaybackEventsReady}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper sx={{ padding: { xs: 2, md: 3 } }} elevation={2}>
                            <Stack spacing={2}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="h6">Master clock subscriptions</Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => setIsClockPanelCollapsed((prev) => !prev)}
                                        aria-label={isClockPanelCollapsed
                                            ? "Expand master clock details"
                                            : "Collapse master clock details"}
                                    >
                                        {isClockPanelCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                                    </IconButton>
                                </Stack>
                                {!isClockPanelCollapsed && (
                                    <>
                                        <Typography variant="body2">
                                            The player now emits time updates that other components can observe. Each
                                            block below is wired to the same player instance using a different
                                            granularity.
                                        </Typography>
                                        <Stack
                                            spacing={2}
                                            direction="row"
                                            flexWrap="wrap"
                                            useFlexGap
                                        >
                                            {Object.entries(CLOCK_LABELS).map(([granularity, label]) => {
                                                const snapshot = clockSnapshots[granularity];
                                                return (
                                                    <Box
                                                        key={granularity}
                                                        sx={{
                                                            borderRadius: 1,
                                                            border: "1px solid",
                                                            borderColor: "divider",
                                                            padding: 2,
                                                            backgroundColor: "grey.50",
                                                            flex: "1 1 260px",
                                                            maxWidth: 320,
                                                        }}
                                                    >
                                                        <Stack spacing={0.5}>
                                                            <Typography variant="subtitle2" color="text.secondary">
                                                                {label}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                Events triggered: {snapshot ? snapshot.count : 0}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                Last timestamp: {snapshot
                                                                    ? `${formatSecondsValue(snapshot.currentTime)} s`
                                                                    : "--"}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                Interval delta: {snapshot
                                                                    ? `${formatMillisecondsValue(snapshot.delta)} ms`
                                                                    : "--"}
                                                            </Typography>
                                                            {granularity === CLOCK_GRANULARITIES.FRAME ? (
                                                                <Typography variant="body2">
                                                                    Frame #: {snapshot && snapshot.frame !== null
                                                                        ? snapshot.frame
                                                                        : "--"}
                                                                </Typography>
                                                            ) : null}
                                                            <Typography variant="caption" color="text.secondary">
                                                                {snapshot
                                                                    ? `Updated ${new Date(snapshot.updatedAt).toLocaleTimeString()}`
                                                                    : "Waiting for events..."}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary">
                                            Clock ready: {isClockReady ? "Yes" : "No"} • Player duration:
                                            {" "}
                                            {formatSecondsValue(clockMeta.duration)} s • Playing:
                                            {" "}
                                            {clockMeta.isPlaying ? "Yes" : "No"}
                                        </Typography>
                                    </>
                                )}
                            </Stack>
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper sx={{ padding: { xs: 2, md: 3 } }} elevation={2}>
                            <Stack spacing={2}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="h6">Playback events</Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => setIsPlaybackPanelCollapsed((prev) => !prev)}
                                        aria-label={isPlaybackPanelCollapsed
                                            ? "Expand playback events"
                                            : "Collapse playback events"}
                                    >
                                        {isPlaybackPanelCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                                    </IconButton>
                                </Stack>
                                {!isPlaybackPanelCollapsed && (
                                    <>
                                        <Typography variant="body2">
                                            Observe high-level playback state changes, including user actions and
                                            buffering interruptions, emitted by the demo player in real time.
                                        </Typography>
                                        <Box
                                            sx={{
                                                borderRadius: 1,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                padding: 2,
                                                backgroundColor: "grey.50",
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {formatPlaybackEventHeadline(recentPlaybackEvent)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {recentPlaybackEvent
                                                    ? `Received ${new Date(recentPlaybackEvent.timestamp).toLocaleTimeString()} • Source: ${describePlaybackEventSource(recentPlaybackEvent)} • Origin: ${recentPlaybackEvent.origin === "user" ? "User" : "System"}`
                                                    : "Waiting for playback events..."}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                borderRadius: 1,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                backgroundColor: "common.white",
                                                maxHeight: 240,
                                                overflowY: "auto",
                                                display: "flex",
                                                flexDirection: "column",
                                            }}
                                        >
                                            {playbackEventsLog.length === 0 ? (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ padding: 1.5 }}
                                                >
                                                    No playback events have fired yet.
                                                </Typography>
                                            ) : (
                                                playbackEventsLog.map((event, index) => (
                                                    <Typography
                                                        key={`${event.timestamp}-${index}`}
                                                        variant="body2"
                                                        sx={{
                                                            paddingX: 1.5,
                                                            paddingY: 0.75,
                                                            borderBottom:
                                                                index === playbackEventsLog.length - 1
                                                                    ? "none"
                                                                    : "1px solid",
                                                            borderColor: "divider",
                                                        }}
                                                    >
                                                        {formatPlaybackEventLogEntry(event)}
                                                    </Typography>
                                                ))
                                            )}
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </Stack>
        </Box>
    );
};

export default SimpleVideoPlayerDemo;
